// store/forms/safetyAuditForm/autosaveErrors/safetyAuditFormAutosaveErrorsActions.js

import { safetyAuditAutosaveErrorsInitialState } from "../safetyAuditFormState";

/**
 * Creates autosave error actions for the Safety Audit form.
 * These actions manage validation and database error state from WebSocket autosave operations.
 * Safety Audit has employee rows, so row error methods are included.
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} tabKey - The tab key for the current form instance
 * @returns {Object} Frozen object containing error management actions
 */
export const makeSafetyAuditAutosaveErrorsActions = (set, get, tabKey) => ({
  /**
   * Set a field-level error from validation failure
   * @param {string} patchType - The patch type (safety_audit_main, safety_audit_employee)
   * @param {string} fieldName - The field that failed validation
   * @param {string} errorMessage - User-friendly error message
   */
  setFieldError(patchType, fieldName, errorMessage) {
    const key = `${patchType}.${fieldName}`;
    set((state) => ({
      [tabKey]: {
        ...state[tabKey],
        safetyAuditForm: {
          ...state[tabKey].safetyAuditForm,
          autosaveErrors: {
            ...state[tabKey].safetyAuditForm.autosaveErrors,
            hasErrors: true,
            lastError: {
              key,
              message: errorMessage,
              timestamp: Date.now(),
              patchType,
            },
            fieldErrors: {
              ...state[tabKey].safetyAuditForm.autosaveErrors.fieldErrors,
              [key]: errorMessage,
            },
          },
        },
      },
    }));
  },

  /**
   * Clear error for a specific field (called on successful save)
   * @param {string} patchType - The patch type
   * @param {string} fieldName - The field to clear
   */
  clearFieldError(patchType, fieldName) {
    const key = `${patchType}.${fieldName}`;
    set((state) => {
      const currentErrors = state[tabKey].safetyAuditForm.autosaveErrors;
      const newFieldErrors = { ...currentErrors.fieldErrors };
      delete newFieldErrors[key];
      const hasRemainingErrors = Object.keys(newFieldErrors).length > 0;
      return {
        [tabKey]: {
          ...state[tabKey],
          safetyAuditForm: {
            ...state[tabKey].safetyAuditForm,
            autosaveErrors: {
              ...currentErrors,
              fieldErrors: newFieldErrors,
              hasErrors: hasRemainingErrors,
              lastError: hasRemainingErrors ? currentErrors.lastError : null,
            },
          },
        },
      };
    });
  },

  /**
   * Clear all errors for a specific patch type
   * @param {string} patchType - The patch type to clear (safety_audit_main, safety_audit_employee)
   */
  clearPatchTypeErrors(patchType) {
    set((state) => {
      const currentErrors = state[tabKey].safetyAuditForm.autosaveErrors;
      const newFieldErrors = Object.fromEntries(
        Object.entries(currentErrors.fieldErrors).filter(
          ([key]) => !key.startsWith(`${patchType}.`)
        )
      );
      const hasRemainingErrors = Object.keys(newFieldErrors).length > 0;
      return {
        [tabKey]: {
          ...state[tabKey],
          safetyAuditForm: {
            ...state[tabKey].safetyAuditForm,
            autosaveErrors: {
              ...currentErrors,
              fieldErrors: newFieldErrors,
              hasErrors: hasRemainingErrors,
              lastError: hasRemainingErrors ? currentErrors.lastError : null,
            },
          },
        },
      };
    });
  },

  /**
   * Get error for a specific field
   * @param {string} patchType - The patch type
   * @param {string} fieldName - The field name
   * @returns {string|null} Error message or null
   */
  getFieldError(patchType, fieldName) {
    const key = `${patchType}.${fieldName}`;
    return (
      get()[tabKey]?.safetyAuditForm?.autosaveErrors?.fieldErrors?.[key] || null
    );
  },

  /**
   * Get all current errors state
   * @returns {Object} Current autosaveErrors state
   */
  getAutosaveErrors() {
    return (
      get()[tabKey]?.safetyAuditForm?.autosaveErrors ||
      safetyAuditAutosaveErrorsInitialState
    );
  },

  /**
   * Reset all autosave errors (called on form load/navigation)
   */
  resetAutosaveErrors() {
    set((state) => ({
      [tabKey]: {
        ...state[tabKey],
        safetyAuditForm: {
          ...state[tabKey].safetyAuditForm,
          autosaveErrors: safetyAuditAutosaveErrorsInitialState,
        },
      },
    }));
  },

  /**
   * Process server error response and set appropriate errors
   * @param {Object} errorData - Structured error from server
   */
  handleServerError(errorData) {
    if (!errorData) return;

    const { error_type, patch_type, field_errors, summary } = errorData;

    if (error_type === "validation" && field_errors) {
      // Set individual field errors
      Object.entries(field_errors).forEach(([field, message]) => {
        const key = `${patch_type}.${field}`;
        set((state) => ({
          [tabKey]: {
            ...state[tabKey],
            safetyAuditForm: {
              ...state[tabKey].safetyAuditForm,
              autosaveErrors: {
                ...state[tabKey].safetyAuditForm.autosaveErrors,
                hasErrors: true,
                lastError: {
                  key,
                  message,
                  timestamp: Date.now(),
                  patchType: patch_type,
                  summary,
                },
                fieldErrors: {
                  ...state[tabKey].safetyAuditForm.autosaveErrors.fieldErrors,
                  [key]: message,
                },
              },
            },
          },
        }));
      });
    } else if (error_type === "database") {
      // Database errors don't have field-level detail
      set((state) => ({
        [tabKey]: {
          ...state[tabKey],
          safetyAuditForm: {
            ...state[tabKey].safetyAuditForm,
            autosaveErrors: {
              ...state[tabKey].safetyAuditForm.autosaveErrors,
              hasErrors: true,
              lastError: {
                key: "database",
                message: summary || "Database error",
                timestamp: Date.now(),
                patchType: patch_type,
                summary,
              },
            },
          },
        },
      }));
    }
  },
});
